import { computed } from 'vue';

import { useApiRequest } from '@peanuds/composables';
import { peanudsApi } from '@peanuds/shared';
import type { Options, VerifyBeneficiaryDataData } from '@peanuds/shared';
import {
  isKnownVerificationReason,
  verificationReasonToMessageKey,
} from './verify-beneficiary.constants';
import { toVerificationViewModel } from './verify-beneficiary.presenter';

export type VerifyBeneficiaryReasonMessageKey =
  (typeof verificationReasonToMessageKey)[keyof typeof verificationReasonToMessageKey];

export const verifyBeneficiaryReasonMessageKeys: VerifyBeneficiaryReasonMessageKey[] =
  Object.values(verificationReasonToMessageKey);
const verifyBeneficiaryReasonMessageKeySet = new Set<string>(verifyBeneficiaryReasonMessageKeys);

export const isVerifyBeneficiaryReasonMessageKey = (
  key: string,
): key is VerifyBeneficiaryReasonMessageKey => verifyBeneficiaryReasonMessageKeySet.has(key);

export const toVerifyBeneficiaryReasonMessageKey = (
  reason: string | null | undefined,
): VerifyBeneficiaryReasonMessageKey | null => {
  if (typeof reason !== 'string' || !isKnownVerificationReason(reason)) {
    return null;
  }

  return verificationReasonToMessageKey[reason];
};

export const useVerifyBeneficiary = () => {
  const {
    execute: verifyBeneficiaryData,
    data: verificationData,
    isLoading: isVerifying,
    error: verifyError,
  } = useApiRequest({
    request: (options: Options<VerifyBeneficiaryDataData>) =>
      peanudsApi.verifyBeneficiaryData(options),
  });

  const verificationResult = computed(() => toVerificationViewModel(verificationData.value?.data));
  const isVerified = computed(() => verificationResult.value.isVerified);

  return {
    verificationResult,
    isVerifying,
    verifyError,
    verifyBeneficiaryData,
    isVerified,
  };
};
